import axios from 'axios'

/**
 * Platform-admin API client.
 *
 * Lives separately from the operator-app axios instance (services/api.js):
 *  - Different baseURL - /api/platform.
 *  - Different localStorage key - `platform_token`, so platform-admin
 *    sessions don't collide with the operator's `token`.
 *  - 401 redirects to /admin/login (not the operator /login).
 *
 * All responses follow `{ success, data, message? }` per the package's
 * controller conventions.
 */
const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/+$/, '')

const platform = axios.create({
  baseURL: `${API_BASE}/api/platform`,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
})

platform.interceptors.request.use((config) => {
  const token = localStorage.getItem('platform_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

platform.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('platform_token')
      localStorage.removeItem('platform_user')
      if (!window.location.pathname.startsWith('/admin/login')) {
        window.location.href = '/admin/login'
      }
    }
    return Promise.reject(error)
  },
)

export default platform

// Blob download helper for CSV / file exports - preserves bearer auth.
export async function downloadPlatformFile(url, fallbackName) {
  const res = await platform.get(url, { responseType: 'blob' })
  let filename = fallbackName
  const disposition = res.headers['content-disposition']
  if (disposition) {
    const match = /filename="?([^"]+)"?/.exec(disposition)
    if (match) filename = match[1]
  }
  const blobUrl = window.URL.createObjectURL(res.data)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(blobUrl)
}

// ─── Auth ─────────────────────────────────────────────────────────
export const platformAuthApi = {
  login:  (data) => platform.post('/auth/login', data),
  me:     ()     => platform.get('/auth/me'),
  logout: ()     => platform.post('/auth/logout'),
}

// ─── Dashboard ────────────────────────────────────────────────────
export const platformDashboardApi = {
  index: () => platform.get('/dashboard'),
}

// ─── Users ────────────────────────────────────────────────────────
export const platformUsersApi = {
  list:    (params)        => platform.get('/users', { params }),
  show:    (id)            => platform.get(`/users/${id}`),
  update:  (id, data)      => platform.put(`/users/${id}`, data),
  ban:     (id, data)      => platform.post(`/users/${id}/ban`, data),
  unban:   (id)            => platform.post(`/users/${id}/unban`),
  remove:  (id)            => platform.delete(`/users/${id}`),
}

// ─── Roles ────────────────────────────────────────────────────────
export const platformRolesApi = {
  list:        ()                 => platform.get('/roles'),
  forUser:     (userId)           => platform.get(`/users/${userId}/roles`),
  assign:      (userId, data)     => platform.post(`/users/${userId}/roles`, data),
  revoke:      (userId, roleName) => platform.delete(`/users/${userId}/roles/${encodeURIComponent(roleName)}`),
}

// ─── Impersonation ────────────────────────────────────────────────
export const platformImpersonationApi = {
  start: (userId) => platform.post(`/users/${userId}/impersonate`),
  stop:  ()       => platform.post('/impersonation/stop'),
}

// ─── Audit log ────────────────────────────────────────────────────
export const platformAuditApi = {
  list:    (params) => platform.get('/audit', { params }),
  actions: ()       => platform.get('/audit/actions'),
  export:  (params) => downloadPlatformFile(`/audit/export?${new URLSearchParams(params || {}).toString()}`, 'audit-log.csv'),
}

// ─── Feature flags ────────────────────────────────────────────────
export const platformFeatureFlagsApi = {
  list:   ()             => platform.get('/feature-flags'),
  create: (data)         => platform.post('/feature-flags', data),
  update: (id, data)     => platform.put(`/feature-flags/${id}`, data),
  remove: (id)           => platform.delete(`/feature-flags/${id}`),
}

// ─── Maintenance ──────────────────────────────────────────────────
export const platformMaintenanceApi = {
  status: ()     => platform.get('/maintenance'),
  update: (data) => platform.put('/maintenance', data),
}

// ─── Announcements ────────────────────────────────────────────────
export const platformAnnouncementsApi = {
  list:        ()             => platform.get('/announcements'),
  activeForMe: ()             => platform.get('/announcements/active'),
  create:      (data)         => platform.post('/announcements', data),
  update:      (id, data)     => platform.put(`/announcements/${id}`, data),
  remove:      (id)           => platform.delete(`/announcements/${id}`),
}

// ─── Subscriptions ────────────────────────────────────────────────
export const platformSubscriptionsApi = {
  list:   (params)      => platform.get('/subscriptions', { params }),
  show:   (id)          => platform.get(`/subscriptions/${id}`),
  cancel: (id, data)    => platform.post(`/subscriptions/${id}/cancel`, data),
  grant:  (data)        => platform.post('/subscriptions/grant', data),
}

// ─── Email templates ──────────────────────────────────────────────
export const platformEmailTemplatesApi = {
  list:     ()             => platform.get('/email-templates'),
  show:     (id)           => platform.get(`/email-templates/${id}`),
  create:   (data)         => platform.post('/email-templates', data),
  update:   (id, data)     => platform.put(`/email-templates/${id}`, data),
  remove:   (id)           => platform.delete(`/email-templates/${id}`),
  preview:  (id, data)     => platform.post(`/email-templates/${id}/preview`, data),
  sendTest: (id, data)     => platform.post(`/email-templates/${id}/send-test`, data),
}

// ─── Email deliverability ─────────────────────────────────────────
export const platformDeliverabilityApi = {
  index: (params) => platform.get('/analytics/email-deliverability', { params }),
}

// ─── AI usage ─────────────────────────────────────────────────────
export const platformAiUsageApi = {
  index: (params) => platform.get('/analytics/ai-usage', { params }),
}

// ─── Integrations ─────────────────────────────────────────────────
export const platformIntegrationsApi = {
  index: () => platform.get('/integrations'),
}

// ─── Exports ──────────────────────────────────────────────────────
export const platformExportsApi = {
  index:  ()     => platform.get('/export'),
  fetch:  (key, params) => downloadPlatformFile(
    `/export/${encodeURIComponent(key)}?${new URLSearchParams(params || {}).toString()}`,
    `${key}.csv`,
  ),
}

// ─── Health ───────────────────────────────────────────────────────
export const platformHealthApi = {
  index:      () => platform.get('/health'),
  flushCache: () => platform.post('/cache/flush'),
}

// ─── Settings ─────────────────────────────────────────────────────
export const platformSettingsApi = {
  all:          ()         => platform.get('/settings'),
  update:       (settings) => platform.put('/settings', { settings }),
  remove:       (key)      => platform.delete(`/settings/${encodeURIComponent(key)}`),
  flushCache:   ()         => platform.post('/settings/cache/flush'),
  testEmail:    (data)     => platform.post('/settings/test/email', data),
  testStripe:   ()         => platform.post('/settings/test/stripe'),
  testRazorpay: ()         => platform.post('/settings/test/razorpay'),
  testPayPal:   ()         => platform.post('/settings/test/paypal'),
  testAi:       (params)   => platform.post('/settings/test/ai', null, { params }),
}
